import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { LDS_AUTOCOMPLETE_MORE } from "../assets/Data/LdsAirportsAutocomplete";
import { resolveLdsSearchCityFromLocation } from "../contexts/SearchContextHelpers";
import {
  RECENT_SEARCH_CATEGORIES,
} from "./agentRecentSearchPayload";
import {
  BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT,
  buildBelvedereCampaignRecentSearchRestoreUrl,
  buildRoyalTulipCampaignRecentSearchRestoreUrl,
  buildRoyalZanzibarCampaignRecentSearchRestoreUrl,
  ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT,
} from "../services/belvedereCampaignEntry";

dayjs.extend(customParseFormat);

const ALL_DESTINATIONS_LOCATION = LDS_AUTOCOMPLETE_MORE[0];

const SUPPORTED_SEARCH_TABS = new Set([
  "planes",
  "hotels",
  "FlightsHotel",
  "OrganizedTour",
  "DynamicPackages",
]);

const DEFAULT_TLV_ORIGIN = {
  COUNTRYNAME_HEB: "ישראל",
  CITYNAME_HEB: "תל אביב",
  AIRPORTCODE: "TLV",
  AIRPORTNAME: 'נתב"ג',
  CITYCODE: "TLV",
  CITYNAME: "TEL AVIV",
  COUNTRYCODE: "IL",
  COUNTRYNAME: "ISRAEL",
};

export function parseAgentRecentSearchRawPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function searchTabFromRecentCategory(category) {
  switch (String(category || "").trim()) {
    case RECENT_SEARCH_CATEGORIES.FLIGHTS:
      return "planes";
    case RECENT_SEARCH_CATEGORIES.HOTELS:
      return "hotels";
    case RECENT_SEARCH_CATEGORIES.VACATION_PACKAGES:
      return "FlightsHotel";
    case RECENT_SEARCH_CATEGORIES.ORGANIZED_TOURS:
      return "OrganizedTour";
    case RECENT_SEARCH_CATEGORIES.DYNAMIC_PACKAGES:
      return "DynamicPackages";
    default:
      return null;
  }
}

function isoDateToDdMmYyyy(iso) {
  if (!iso) return null;
  const d = dayjs(String(iso).slice(0, 10), "YYYY-MM-DD", true);
  return d.isValid() ? d.format("DD/MM/YYYY") : null;
}

function normalizeRestoreRooms(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return { rooms: [{ adults: 2, kids: 0, infants: 0, kidsAges: [] }] };
  }
  const normalized = rooms.map((room) => {
    if (!room || typeof room !== "object") {
      return { adults: 2, kids: 0, infants: 0, kidsAges: [] };
    }
    const adults = Number(room.adults ?? room.AdultsCount) || 0;
    const kids = Number(room.kids ?? room.children ?? room.KidsCount) || 0;
    const infants = Number(room.infants ?? room.InfantsCount) || 0;
    let kidsAges = room.kidsAges ?? room.KidsAges ?? [];
    if (!Array.isArray(kidsAges)) kidsAges = [];
    const ages = [...kidsAges];
    while (ages.length < kids) ages.push(2);
    if (ages.length > kids) ages.length = kids;
    return {
      adults: adults > 0 ? adults : 2,
      kids,
      infants,
      kidsAges: ages,
    };
  });
  return { rooms: normalized };
}

function sanitizeLocationFields(loc) {
  if (!loc || typeof loc !== "object") return null;
  const out = {};
  for (const [key, value] of Object.entries(loc)) {
    if (value == null || value === "") continue;
    out[key] = typeof value === "string" ? value.trim() : value;
  }
  const cityCode = String(out.CITYCODE ?? "").trim().toUpperCase();
  if (
    cityCode &&
    cityCode !== "ALL" &&
    !String(out.AIRPORTCODE ?? "").trim()
  ) {
    out.AIRPORTCODE = out.CITYCODE;
  }
  return Object.keys(out).length ? out : null;
}

function isAllDestinationsRestoreCandidate(loc, row) {
  if (loc && typeof loc === "object") {
    if (resolveLdsSearchCityFromLocation(loc).isAllDestinations) return true;
  }
  const label = String(
    row?.destination_label || loc?.fullname || loc?.COUNTRYNAME_HEB || "",
  ).trim();
  const code = String(
    row?.destination_code || loc?.CITYCODE || loc?.AIRPORTCODE || "",
  )
    .trim()
    .toUpperCase();
  if (code === "ALL") return true;
  if (label === "כל היעדים") return true;
  if (label.toLowerCase() === "all destinations") return true;
  return false;
}

/** Canonical LDS "כל היעדים" object for restore + display. */
export function normalizeRestoreLocation(raw, row) {
  const rawLoc = raw?.primaryLocation || raw?.location;
  if (isAllDestinationsRestoreCandidate(rawLoc, row)) {
    return { ...ALL_DESTINATIONS_LOCATION };
  }

  const sanitized = sanitizeLocationFields(rawLoc);
  if (sanitized) {
    if (isAllDestinationsRestoreCandidate(sanitized, row)) {
      return { ...ALL_DESTINATIONS_LOCATION };
    }
    return sanitized;
  }

  const label = String(row?.destination_label || "").trim();
  const code = String(row?.destination_code || "").trim();
  if (!label && !code) return null;

  const fallback = sanitizeLocationFields({
    fullname: label || code,
    ...(code ? { CITYCODE: code, AIRPORTCODE: code } : {}),
    ...(label ? { CITYNAME_HEB: label } : {}),
  });
  if (!fallback) return null;
  if (isAllDestinationsRestoreCandidate(fallback, row)) {
    return { ...ALL_DESTINATIONS_LOCATION };
  }
  return fallback;
}

function resolveRestoreLocation(raw, row) {
  return normalizeRestoreLocation(raw, row);
}

function resolveRestoreDates(raw, row) {
  let arrival =
    raw?.calendarDateArrival != null
      ? String(raw.calendarDateArrival).trim() || null
      : null;
  let leave =
    raw?.calendarDateLeave != null
      ? String(raw.calendarDateLeave).trim() || null
      : null;

  if (!arrival && row?.departure_date) {
    arrival = isoDateToDdMmYyyy(row.departure_date);
  }
  if (!leave && row?.return_date) {
    leave = isoDateToDdMmYyyy(row.return_date);
  }
  return { arrival, leave };
}

function resolveTourMonth(raw, row) {
  const explicit = [
    raw?.tourMonth,
    raw?.organizedTourMonth,
    raw?.monthYear,
    raw?.departureMonth,
  ].find((v) => v != null && String(v).trim());
  if (explicit) return String(explicit).trim();

  if (raw?.calendarDateArrival && String(raw.calendarDateArrival).includes("/")) {
    const mm = dayjs(String(raw.calendarDateArrival).trim(), "MM/YYYY", true);
    if (mm.isValid()) return mm.format("MM/YYYY");
  }
  if (row?.departure_date) {
    const d = dayjs(String(row.departure_date).slice(0, 10), "YYYY-MM-DD", true);
    if (d.isValid()) return d.format("MM/YYYY");
  }
  return "";
}

/**
 * Pure restore plan from a GET /recent-searches row.
 * @returns {{ ok: true, state: object } | { ok: false, reason: string }}
 */
export function resolveAgentRecentSearchRestorePlan(row) {
  if (!row || typeof row !== "object") {
    return { ok: false, reason: "missing_row" };
  }

  const raw = parseAgentRecentSearchRawPayload(row.raw_payload);
  if (!raw || typeof raw !== "object") {
    return { ok: false, reason: "missing_raw_payload" };
  }

  if (raw.variant === BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT) {
    const url = buildBelvedereCampaignRecentSearchRestoreUrl(raw);
    if (!url) {
      return { ok: false, reason: "invalid_campaign_payload" };
    }
    return {
      ok: true,
      state: { variant: BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT, url },
    };
  }

  if (raw.variant === ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT) {
    const url = buildRoyalTulipCampaignRecentSearchRestoreUrl(raw);
    if (!url) {
      return { ok: false, reason: "invalid_campaign_payload" };
    }
    return {
      ok: true,
      state: { variant: ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT, url },
    };
  }

  if (raw.variant === ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT) {
    const url = buildRoyalZanzibarCampaignRecentSearchRestoreUrl(raw);
    if (!url) {
      return { ok: false, reason: "invalid_campaign_payload" };
    }
    return {
      ok: true,
      state: { variant: ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT, url },
    };
  }

  if (raw.variant === "monthly_hotel_lds_grid") {
    const hotelId = raw.hotelId ?? row.destination_code ?? null;
    const month = Number(raw.month);
    const year = Number(raw.year);
    if (!hotelId || !month || !year) {
      return { ok: false, reason: "invalid_monthly_hotel_payload" };
    }
    return {
      ok: true,
      state: {
        variant: "monthly_hotel_lds_grid",
        hotelId,
        hotelName: raw.hotelName ?? row.destination_label ?? null,
        month,
        year,
        monthsCount: Number(raw.monthsCount) > 0 ? Number(raw.monthsCount) : 1,
        roomInfo: normalizeRestoreRooms(raw.roomInfoRooms),
      },
    };
  }

  const category = String(row.category || "").trim();
  if (
    category === RECENT_SEARCH_CATEGORIES.CRUISE_PAYMENT ||
    category === RECENT_SEARCH_CATEGORIES.UNKNOWN
  ) {
    return { ok: false, reason: "unsupported_category" };
  }

  const searchTab =
    (raw.searchTab && SUPPORTED_SEARCH_TABS.has(String(raw.searchTab))
      ? String(raw.searchTab)
      : null) || searchTabFromRecentCategory(category);

  if (!searchTab || !SUPPORTED_SEARCH_TABS.has(searchTab)) {
    return { ok: false, reason: "unsupported_search_tab" };
  }

  const location = resolveRestoreLocation(raw, row);
  if (!location) {
    return { ok: false, reason: "missing_destination" };
  }

  const { arrival, leave } = resolveRestoreDates(raw, row);
  const roomInfo = normalizeRestoreRooms(raw.roomInfoRooms);
  const origin =
    raw.origin && typeof raw.origin === "object" && Object.keys(raw.origin).length
      ? raw.origin
      : DEFAULT_TLV_ORIGIN;

  const state = {
    searchTab,
    location,
    origin,
    roomInfo,
    flightTripType:
      raw.flightTripType === "OneWay" || raw.flightTripType === "RoundTrip"
        ? raw.flightTripType
        : searchTab === "DynamicPackages"
          ? "RoundTrip"
          : "RoundTrip",
    calendarDateArrival: arrival || "",
    calendarDateLeave: leave || "",
    tourMonth: searchTab === "OrganizedTour" ? resolveTourMonth(raw, row) : "",
    packageDateMode:
      raw.packageDateMode === "month" ? "month" : "exact",
    packageMonth:
      raw.packageMonth != null ? String(raw.packageMonth).trim() : "",
  };

  if (searchTab === "OrganizedTour" && !String(state.tourMonth).trim()) {
    return { ok: false, reason: "missing_tour_month" };
  }

  if (
    searchTab === "planes" ||
    searchTab === "DynamicPackages" ||
    searchTab === "FlightsHotel"
  ) {
    const needsReturn =
      searchTab === "FlightsHotel" ||
      state.flightTripType === "RoundTrip";

    if (!String(state.calendarDateArrival).trim()) {
      return { ok: false, reason: "missing_departure_date" };
    }
    if (needsReturn && !String(state.calendarDateLeave).trim()) {
      return { ok: false, reason: "missing_return_date" };
    }
  }

  if (
    searchTab === "FlightsHotel" &&
    state.packageDateMode === "month" &&
    !String(state.packageMonth).trim()
  ) {
    const monthFromArrival = dayjs(
      String(state.calendarDateArrival).trim(),
      "DD/MM/YYYY",
      true,
    );
    if (monthFromArrival.isValid()) {
      state.packageMonth = monthFromArrival.format("MM/YYYY");
    }
  }

  return { ok: true, state };
}
